import { useProjectStateStore } from '@client/store/project/project.store';
import { getProjectMapPreview } from '@shared/helpers/design/building-map-preview.helpers';
import { safeMartinezUnion } from '@shared/helpers/geometry/martinez-polygon-clipping-ops.helpers';
import { isProjectInfo } from '@shared/helpers/project-folder.helpers';
import { snapshotCanvasToDataUrl } from '@client/helpers/canvas-snapshot.helpers';
import {
  captureProjectOpeningCamera,
  prepareProjectClosingCamera,
} from '@features/design/helpers/map-camera-handoff.helpers';

import {
  prepareProjectOpeningMap,
  useProjectClosing,
  waitForProjectClosingListRender,
  useProjectOpening,
  waitForProjectOpeningRender,
} from '../../project-opening.store';

/** Load on the live list, render Design invisibly, then reveal before pitching. */
export const openProjectWithAnimation = async (
  source: Element,
  loadProject: (cameraReady: Promise<void>) => Promise<unknown>,
  navigateToProject: () => Promise<void>,
  projectId: number,
  returnToList: (url: string) => Promise<void> | void,
): Promise<void> => {
  if (useProjectOpening.getState().phase !== 'idle') return;
  const page = source.closest<HTMLElement>('[data-project-list-page]');
  if (!page) {
    await loadProject(Promise.resolve());
    await navigateToProject();
    return;
  }
  const listUrl = window.location.pathname + window.location.search;
  useProjectOpening.setState({ phase: 'loading', projectId, error: undefined });
  let renderWait: ReturnType<typeof waitForProjectOpeningRender> | undefined;
  let navigated = false;
  try {
    // Both start in the click turn; the route and real list remain intact.
    const cameraReady = prepareProjectOpeningMap(projectId).then(
      () =>
        new Promise<void>((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
        ),
    );
    await Promise.all([cameraReady, loadProject(cameraReady)]);
    captureProjectOpeningCamera(projectId);
    renderWait = waitForProjectOpeningRender();
    useProjectOpening.setState({ phase: 'rendering' });
    navigated = true;
    await navigateToProject();
    await renderWait.ready;
    useProjectOpening.setState({ phase: 'revealing' });
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    const content = document.querySelector<HTMLElement>(
      '[data-project-opening-surface]',
    );
    if (
      content &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      const fade = content.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 300,
        easing: 'ease-out',
        fill: 'forwards',
      });
      try {
        await fade.finished;
      } finally {
        fade.cancel();
      }
    }
  } catch (error) {
    if (navigated) await returnToList(listUrl);
    useProjectOpening.setState({
      error: error instanceof Error ? error.message : 'Could not open project',
    });
  } finally {
    renderWait?.dispose();
    useProjectOpening.setState({ phase: 'idle', projectId: undefined });
  }
};

let returningToList = false;

/** Fade Design's chrome during the camera move, then reveal the positioned list. */
export const closeProjectWithAnimation = async (
  projectId: number,
  navigateToList: () => void,
): Promise<void> => {
  if (returningToList) return;
  returningToList = true;
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  let renderWait:
    ReturnType<typeof waitForProjectClosingListRender> | undefined;
  let fade: Animation | undefined;
  let background: HTMLElement | undefined;
  let header: HTMLElement | undefined;
  const visibility = document.createElement('style');
  const exitAppearance = document.createElement('style');
  const overlay = document.createElement('div');
  const chrome = document.querySelectorAll<HTMLElement>(
    '[data-app-header], [data-project-opening-content] aside[aria-label="Storeys"], [data-design-exit-panel]',
  );
  try {
    useProjectClosing.setState({ showLoading: true });
    chrome.forEach((element) =>
      element.setAttribute('data-project-exit-chrome', ''),
    );
    exitAppearance.textContent = `
      @keyframes project-exit-chrome { to { opacity: 0; } }
      @keyframes project-exit-tint { from { opacity: 0; } to { opacity: 0.8; } }
      [data-project-exit-chrome] {
        animation: project-exit-chrome ${reducedMotion ? 0 : 200}ms ease-out forwards;
        pointer-events: none !important;
      }
      [data-project-exit-overlay] {
        position: fixed; inset: 0; z-index: 2000; pointer-events: none;
        background: #E2E2D6; opacity: 0.8;
        animation: project-exit-tint ${reducedMotion ? 0 : 650}ms ease-in-out forwards;
      }
    `;
    overlay.setAttribute('data-project-exit-overlay', '');
    overlay.setAttribute('aria-hidden', 'true');
    document.head.append(exitAppearance);
    document.body.append(overlay);
    const { project, projectsLookup } = useProjectStateStore.getState();
    const preview =
      project.id === projectId
        ? getProjectMapPreview(project.design, safeMartinezUnion)
        : projectsLookup[projectId]?.map_preview;
    const camera = await prepareProjectClosingCamera(
      reducedMotion,
      projectId,
      preview,
      Object.values(projectsLookup).filter(isProjectInfo).length,
    );
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    const content = document.querySelector<HTMLElement>(
      '[data-project-opening-content]',
    );
    const contentSnapshot = content?.cloneNode(true);
    background =
      contentSnapshot instanceof HTMLElement ? contentSnapshot : undefined;
    if (background && content) {
      const bounds = content.getBoundingClientRect();
      background.removeAttribute('data-project-opening-content');
      background.setAttribute('data-project-closing-background', '');
      background
        .querySelectorAll('[data-app-header]')
        .forEach((element) => element.removeAttribute('data-app-header'));
      background
        .querySelectorAll('[id]')
        .forEach((element) => element.removeAttribute('id'));
      // Canvas pixels are not copied by cloneNode, including the Design map.
      const canvases = background.querySelectorAll('canvas');
      content.querySelectorAll('canvas').forEach((canvas, index) => {
        const dataUrl = snapshotCanvasToDataUrl(canvas);
        const target = canvases[index];
        if (!dataUrl || !target) return;
        const image = document.createElement('img');
        image.src = dataUrl;
        image.alt = '';
        image.className = canvas.className;
        image.style.cssText = canvas.style.cssText;
        const appearance = getComputedStyle(canvas);
        image.style.width = appearance.width;
        image.style.height = appearance.height;
        target.replaceWith(image);
      });
      background.inert = true;
      background.setAttribute('aria-hidden', 'true');
      background.style.cssText += `;position:fixed;top:${bounds.top}px;left:${bounds.left}px;width:${bounds.width}px;height:${bounds.height}px;z-index:1999;pointer-events:none;opacity:1;`;
      document.body.append(background);
      const targets = background.querySelectorAll('*');
      content.querySelectorAll('*').forEach((element, index) => {
        const target = targets[index];
        if (!target) return;
        target.scrollTop = element.scrollTop;
        target.scrollLeft = element.scrollLeft;
      });
      background.scrollTop = content.scrollTop;
    }
    const projectHeader =
      document.querySelector<HTMLElement>('[data-app-header]');
    const headerSnapshot = projectHeader?.cloneNode(true);
    if (headerSnapshot instanceof HTMLElement && projectHeader) {
      header = headerSnapshot;
      header.removeAttribute('data-app-header');
      header.inert = true;
      header.setAttribute('aria-hidden', 'true');
      const bounds = projectHeader.getBoundingClientRect();
      header.style.cssText += `;position:fixed;top:${bounds.top}px;left:${bounds.left}px;width:${bounds.width}px;height:${bounds.height}px;pointer-events:none;`;
      background?.append(header);
    }
    if (background) {
      background
        .querySelectorAll<HTMLElement>('[data-project-exit-chrome]')
        .forEach((element) => {
          element.style.opacity = '0';
          element.style.animation = 'none';
        });
      // Carry the completed tint through the same crossfade as the map snapshot.
      overlay.style.animation = 'none';
      background.append(overlay);
    }
    useProjectClosing.setState({ phase: 'preparing', projectId, camera });
    visibility.textContent =
      '[data-project-list-page] [role="tree"], [data-project-list-header-sort], [data-project-list-view-tabs], [data-project-list-header-search] { opacity: 0 !important; pointer-events: none !important; }';
    document.head.append(visibility);
    renderWait = waitForProjectClosingListRender();
    navigateToList();
    const row = await waitForProjectListRow(projectId);
    if (row) {
      const bounds = row.getBoundingClientRect();
      const pageTop =
        row.closest('[data-project-list-page]')?.getBoundingClientRect().top ??
        0;
      if (bounds.top < pageTop || bounds.bottom > window.innerHeight)
        row.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
    await renderWait.ready;
    useProjectClosing.setState({ showLoading: false });
    if (background && !reducedMotion) {
      fade = background.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 250,
        easing: 'ease-out',
        fill: 'forwards',
      });
      await fade.finished;
    }
  } finally {
    fade?.cancel();
    background?.remove();
    overlay.remove();
    chrome.forEach((element) =>
      element.removeAttribute('data-project-exit-chrome'),
    );
    exitAppearance.remove();
    visibility.remove();
    renderWait?.dispose();
    // Reveal the already-positioned list without a second camera movement.
    useProjectClosing.setState({ phase: 'restoring' });
    useProjectClosing.setState({
      phase: 'idle',
      projectId: undefined,
      camera: undefined,
      showLoading: false,
    });
    returningToList = false;
  }
};

/** Wait for the real list layout, with a fallback when the project is no longer listed. */
const waitForProjectListRow = (
  projectId: number,
): Promise<HTMLElement | null> =>
  new Promise((resolve) => {
    const selector = `[data-project-list-page] [data-project-list-row-id="${projectId}"]`;
    const finish = (row: HTMLElement | null) => {
      observer.disconnect();
      clearTimeout(timeout);
      resolve(row);
    };
    const check = () => {
      const row = document.querySelector<HTMLElement>(selector);
      if (row) finish(row);
    };
    const observer = new MutationObserver(check);
    const timeout = setTimeout(() => finish(null), 5000);
    observer.observe(document.body, { childList: true, subtree: true });
    check();
  });
